import { get, writable } from "svelte/store";
import { trackGtagEvent } from "../helpers/gtag.js";
import isMobile from "../helpers/mobile.js";
import persisted from "./common/persisted.js";

export const PADDING = 20.0;

export const availableWindows = [
	{
		kind: "info",
		title: "Status",
		width: 275,
		height: 250,
		min: { width: 200 },
		max: { width: Infinity },
		sizeToFit: true
	},
	{
		kind: "metrics",
		title: "Metrics",
		width: 230,
		height: 230,
		min: { width: 230 },
		max: { width: Infinity },
		sizeToFit: true
	},
	{
		kind: "journal",
		title: "Journal",
		width: 270,
		height: 250,
		min: { width: 200, height: 202 },
		isHidden: false,
		isHoverable: true
	},
	{
		kind: "vehicle",
		title: "Vehicle",
		width: 210,
		height: 180,
		min: { width: 210 },
		max: { width: 180 },
		sizeToFit: false
	},
	{
		kind: "article",
		title: "Encyclopedia",
		width: 250,
		height: 212,
		min: { width: 200, height: 202 },
		isHoverable: true
	},
	{
		kind: "classifieds",
		title: "Classifieds",
		width: 230,
		height: 192,
		min: { width: 200, height: 202 },
		isHoverable: true
	},
	{
		kind: "food",
		title: "Food",
		width: 270,
		height: 250,
		min: { width: 200, height: 202 },
		isHidden: false,
		isHoverable: true
	},
	{
		kind: "news",
		title: "News",
		width: 240,
		height: 202,
		min: { width: 200, height: 202 }
	},
	{
		kind: "photos",
		title: "Camera Roll",
		width: 400,
		height: 360,
		min: { width: 290, height: 200 }
	},
	{
		kind: "onboarding",
		title: "America Setup Wizard",
		width: 745,
		height: 470,
		isHidden: true,
		isModal: true
	},
	{
		kind: "color",
		title: "",
		width: 120,
		height: 178,
		min: { width: 120 },
		max: { width: 120 },
		isHidden: true
	},
	{
		kind: "photo",
		title: "Photo",
		width: 600,
		height: 400,
		min: { width: 200, height: 200 },
		isHidden: true
	},
	{
		kind: "radio",
		title: "Radio",
		width: 260,
		height: 250,
		min: { width: 260 },
		max: { width: 260 },
		sizeToFit: true
	},
	{
		kind: "talkie",
		title: "Talkie",
		width: 230,
		height: 200,
		min: { width: 230 },
		max: { width: 230 },
		sizeToFit: true
	},
	{
		kind: "map",
		title: "Map",
		width: 300,
		height: 300,
		min: { width: 200, height: 200 }
	},
	{
		kind: "settings",
		title: "Settings",
		width: 240,
		height: 113,
		min: { width: 240 },
		max: { width: 200 },
		sizeToFit: true
	},
	{
		kind: "about",
		title: "About",
		width: 385,
		height: 250,
		min: { width: 350, height: 200 },
		max: { width: 500, height: 300 }
	},
	{
		kind: "arrival",
		title: "Arrival",
		width: 240,
		height: 250,
		isHidden: true,
		sizeToFit: true
	},
	{
		kind: "report",
		title: "Feedback",
		width: 300,
		height: 200,
		min: { width: 200, height: 200 },
		isHidden: true
	},
	{
		kind: "cairn",
		title: "Cairn",
		width: 320,
		height: 225,
		min: { width: 320, height: 225 },
		max: { width: 500, height: 320 },
		isHidden: true
	},
	{
		kind: "build-cairn",
		title: "Build a Cairn",
		width: 360,
		height: 275,
		min: { width: 360, height: 275 },
		max: { width: 500, height: 320 },
		isHidden: true
	}
];

function ordered(windows) {
	if (!isMobile) {
		return windows;
	}

	let y = 20.0;

	return windows
		.filter(({ isClosed }) => !isClosed)
		.sort((a, b) => (a.z < b.z ? -1 : 1))
		.map((window) => {
			const next = y;
			y += PADDING + window.height;
			return { ...window, y: next };
		});
}

function constrained(windows) {
	return windows.map((window) => {
		const next = {
			...window,
			x: Math.max(Math.min(window.x, document.body.clientWidth - window.width - PADDING), PADDING),
			y: Math.max(Math.min(window.y, globalThis.innerHeight - window.height - PADDING), PADDING),
			width: Math.max(Math.min(window.width, document.body.clientWidth - PADDING * 2.0), window.min?.width || 0.0)
		};

		if (isMobile) {
			next.x = (document.body.clientWidth - next.width) * 0.5;
			next.y = window.y;
		}

		if (next.x === window.x && next.y === window.y) {
			return window;
		}

		return next;
	});
}

function updateLayoutOnViewportResize(windows) {
	function didResizeWindow() {
		windows.update((state) => constrained(state));
	}

	window.addEventListener("resize", didResizeWindow);

	didResizeWindow();
}

function createWindowsStore() {
	const store = writable([]);
	const { subscribe, set, update } = store;
	const openSubscribers = [];

	function focus(id) {
		if (isMobile) {
			return;
		}

		update((state) => {
			const z = (Math.max(...state.map((window) => window.z || 0)) || 0) + 1;

			return state.map((window) => {
				if (window.id === id) {
					return { ...window, z, isFocused: true };
				} else if (window.isFocused) {
					return { ...window, isFocused: false };
				}

				return window;
			});
		});
	}

	return {
		subscribe,
		update,

		set(state) {
			set(
				state.filter(({ kind, isClosed }) => isClosed !== true && availableWindows.some((w) => w.kind === kind))
			);
		},

		open(kind, props = {}) {
			const canOpen = !!availableWindows.find((window) => window.kind === kind);

			if (!canOpen) {
				return;
			}

			const id = Math.random().toString(16).slice(2, 7);
			const { innerHeight } = window;

			update((state) => {
				const window = {
					...availableWindows.find((window) => window.kind === kind),
					id,
					props
				};

				window.z = props.z ? props.z : Math.max(Math.max(...state.map(({ z }) => z || 0)) + 1, 1);

				window.x = (document.body.clientWidth - window.width) * 0.5;

				if (isMobile) {
					window.isFocused = true;
				} else {
					window.y = (innerHeight - window.height) * 0.5;

					const existing = state.filter((window) => !window.isClosed && window.kind === kind).at(-1);

					if (existing) {
						window.x = existing.x + (existing.isClosed ? 0.0 : PADDING);
						window.y = existing.y + (existing.isClosed ? 0.0 : PADDING);
					}
				}

				return ordered(constrained([...state, window]));
			});

			if (isMobile && kind !== "report") {
				setTimeout(() => {
					update((state) => {
						const existing = state.find((window) => id === window.id);

						if (!existing) {
							return state;
						}

						const { height, y } = existing;
						const scrollTop = globalThis.scrollY;
						const viewportHeight = globalThis.innerHeight;
						const top = y + height - viewportHeight + PADDING * 3.0 + 5.0;

						if (y + height > scrollTop + viewportHeight) {
							window.scrollTo({
								top,
								behavior: "smooth"
							});
						}

						return state;
					});
				}, 100);
			}

			openSubscribers.forEach(([windowKind, handler]) => {
				if (windowKind !== kind) {
					return;
				}

				handler(id);
			});

			trackGtagEvent("window_open", { window_kind: kind });

			return id;
		},

		focus,

		move(id, x, y) {
			update((state) =>
				constrained(
					state.map((window) => {
						if (window.id === id) {
							return { ...window, x, y };
						}

						return window;
					})
				)
			);
		},

		resize(id, width, height) {
			update((state) =>
				ordered(
					constrained(
						state.map((window) => {
							if (window.id === id) {
								return { ...window, width, height };
							}

							return window;
						})
					)
				)
			);
		},

		close(id) {
			const closing = get(store).find((window) => window.id === id);

			if (closing && closing.isClosed !== true) {
				trackGtagEvent("window_close", { window_kind: closing.kind });
			}

			let frontmostWindow;

			update((state) => {
				const next = state.map((window) => ({
					...window,
					isClosed: window.isClosed || window.id === id
				}));

				frontmostWindow = next
					.filter((window) => !window.isClosed)
					.sort((a, b) => (a.z < b.z ? -1 : 1))
					.at(-1);

				return ordered(next);
			});

			if (frontmostWindow) {
				focus(frontmostWindow.id);
			}
		},

		hover(id) {
			update((state) =>
				state.map((window) => {
					if (window.id === id) {
						return { ...window, isHovered: true };
					}

					return window;
				})
			);
		},

		unhover(id) {
			update((state) =>
				state.map((window) => {
					if (window.id === id) {
						return { ...window, isHovered: false };
					}

					return window;
				})
			);
		},

		isOpen(kind) {
			return get(store).find((w) => w.kind === kind && w.isClosed !== true) !== undefined;
		},

		onOpen(kind, handler) {
			openSubscribers.push([kind, handler]);
		}
	};
}

const windows = persisted("windows:v7", true, createWindowsStore());

updateLayoutOnViewportResize(windows);

export default windows;
